import React, { useEffect, useMemo, useState } from "react";
import { Alert, Spinner } from "react-bootstrap";
import API from "../../services/api";
import { fmtDate, orDash } from "./AccountsShared";
import "./AccountsPayments.css";

const money = (value) =>
  `₹ ${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const label = (value = "") =>
  String(value || "pending")
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());

const initials = (name = "") => {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "NA";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

export const AccountsSupplierPayables = () => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [query, setQuery] = useState("");

  const load = async () => {
    try {
      setLoading(true);
      setErr("");
      const res = await API.get("/employee/accounts/payment-desk");
      const payables = Array.isArray(res.data?.supplierPayables) ? res.data.supplierPayables : [];
      setRows(payables.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0)));
      setSummary(res.data?.summary || {});
    } catch (e) {
      setErr(e?.response?.data?.message || "Failed to load supplier payable payments");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) =>
      [row.partyName, row.referenceNo, row.status, row.note, row.amount]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [rows, query]);

  const totalAmount = rows.reduce((sum, row) => sum + Number(row.amount || 0), 0);
  const readyCount = rows.filter((row) =>
    ["accounts-ready", "supplier-agreed"].includes(String(row.status || "").toLowerCase())
  ).length;

  return (
    <div className="payments-page">
      <div className="payments-head">
        <div>
          <h2 className="payments-title">Supplier Payables</h2>
          <p className="payments-subtitle">Supplier ko jo quality approved payment karni hai, woh yahan separate dikhegi.</p>
        </div>
        <button className="payments-primary" type="button" onClick={load}>
          <span className="material-symbols-outlined">refresh</span>
          Refresh
        </button>
      </div>

      <div className="payments-toolbar">
        <div className="payments-search">
          <span className="material-symbols-outlined">search</span>
          <input placeholder="Search supplier, PO, vehicle, report..." value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
      </div>

      {err && <Alert variant="danger" className="mx-4">{err}</Alert>}

      <div className="payments-stats">
        <div className="stat-card">
          <p>Total Supplier Payable</p>
          <h3>{money(summary.supplierPayableAmount ?? totalAmount)}</h3>
          <span className="muted">{summary.supplierPayableCount || rows.length} payable item(s)</span>
        </div>
        <div className="stat-card">
          <p>Ready For Payment</p>
          <h3>{readyCount}</h3>
          <span className="trend positive"><span className="material-symbols-outlined">task_alt</span> Accounts can process</span>
        </div>
        <div className="stat-card">
          <p>Quality Linked</p>
          <h3>{rows.length}</h3>
          <span className="muted">Plant approved / supplier agreed rows</span>
        </div>
      </div>

      <div className="payments-table-card">
        {loading ? (
          <div className="payments-loading"><Spinner animation="border" /></div>
        ) : (
          <div className="payments-table-wrap">
            <table className="payments-table">
              <thead>
                <tr>
                  <th>Supplier</th>
                  <th>Quality / PO Ref</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Forwarded</th>
                  <th>Material / Vehicle</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.length === 0 ? (
                  <tr><td colSpan={6} className="payments-empty">No supplier payable pending</td></tr>
                ) : filteredRows.map((row) => (
                  <tr key={`${row.source}-${row.id}`}>
                    <td>
                      <div className="vendor-cell">
                        <div className="avatar">{initials(row.partyName)}</div>
                        <span>{orDash(row.partyName)}</span>
                      </div>
                    </td>
                    <td className="muted">{orDash(row.referenceNo)}</td>
                    <td className="amount">{money(row.amount)}</td>
                    <td><span className="status-pill pending"><span className="dot" />{label(row.status)}</span></td>
                    <td className="muted">{fmtDate(row.date)}</td>
                    <td className="muted">{orDash(row.note)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountsSupplierPayables;
